import { useEffect, useMemo, useState } from "react";
import ContributorMetrcisView from "./ContributorMetrcisView";
import { api } from "../../../services/backend/api";
import dayjs from "dayjs";
import { message } from "antd";

const getRangeByWeekEndingDate = (weekEndingDate) => {
  const start = dayjs(weekEndingDate).subtract(6, "days").format("YYYY-MM-DD");
  const end = dayjs(weekEndingDate).format("YYYY-MM-DD");
  return { start, end };
};

const ContributorMetrics = ({
  projectId,
  objectiveId,
  range,
  weekEndingDate,
  isWeekly,
  refreshTrigger = 0,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const dateRange = useMemo(
    () => (isWeekly ? getRangeByWeekEndingDate(weekEndingDate) : range),
    [isWeekly, weekEndingDate, range],
  );

  // Load contributor metrics when timeframe, target, or weekly/daily changes
  useEffect(() => {
    let cancelled = false;
    if (!projectId && !objectiveId) return;
    setLoading(true);

    const basePath = objectiveId
      ? `/performance/po/contributor-daily-metrics/${objectiveId}/range`
      : `/performance/p/contributor-daily-metrics/${projectId}/range`;
    const isRefresh = Boolean(refreshTrigger);

    const qs = `start=${dateRange.start}&end=${dateRange.end}${isRefresh ? "&refresh=true" : ""}`;
    api
      .get(`${basePath}?${qs}`)
      .then((response) => {
        if (cancelled) return;
        let list = [];
        let refreshedAt = null;
        if (response && response.success && Array.isArray(response.data)) {
          list = response.data;
        } else if (
          response &&
          !Array.isArray(response) &&
          Array.isArray(response.data)
        ) {
          list = response.data;
          if (typeof response.lastRefreshedAt === "string") {
            refreshedAt = response.lastRefreshedAt;
          }
        } else if (Array.isArray(response)) {
          list = response;
        }
        setLastRefreshedAt(refreshedAt);

        // Group by week end date and contributor id
        const uniqueContributors = new Map();
        list.forEach((item) => {
          const weekEnd = item.week_ending_date;
          const contributorId = item.contributor_id;
          if (!uniqueContributors.has(`${weekEnd}-${contributorId}`)) {
            const payload = {
              week_ending_date: weekEnd,
              calendar_date: item.calendar_date,
              contributor_id: contributorId,
              contributor_name: item.contributor_name,
              email: item.email,
              working_days: 1,
              total_hours: Number(item.cp_current_week_hours).toFixed(2),
              total_units: Number(item.cp_current_week_units).toFixed(2),
              average_rph: Number(item.cp_current_week_avg_rph).toFixed(2),
              daily_rph: Number(item.daily_rph).toFixed(2),
            };
            uniqueContributors.set(`${weekEnd}-${contributorId}`, payload);
          } else {
            const payload = uniqueContributors.get(
              `${weekEnd}-${contributorId}`,
            );
            payload.working_days += 1;
            uniqueContributors.set(`${weekEnd}-${contributorId}`, payload);
          }
        });

        setData(Array.from(uniqueContributors.values()));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`Error loading contributor metrics:`, err);
        message.error(`Failed to load contributor metrics`);
        setData([]);
        setLastRefreshedAt(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWeekly, projectId, objectiveId, dateRange.start, dateRange.end, refreshTrigger]);
  return (
    <ContributorMetrcisView
      data={data}
      loading={loading}
      isWeekly={isWeekly}
      startDate={dateRange.start}
      endDate={dateRange.end}
      lastRefreshedAt={lastRefreshedAt}
      onRefresh={onRefresh}
    />
  );
};

export default ContributorMetrics;
